import React, { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Calendar, MapPin, Ticket, Clock } from 'lucide-react';
import { Footer } from '../components/Footer';
import { ScrollToTop } from '../components/ScrollToTop';
import { events, eventCategories, isEventPast, getUpcomingEvents, getPastEvents } from '../data/events';
import type { Event } from '../data/events';
export function EventsPage() {
  const [selectedCategory, setSelectedCategory] = useState<string>('All');
  const [sortBy, setSortBy] = useState<'date' | 'name'>('date');
  const [showPastEvents, setShowPastEvents] = useState(false);
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const upcomingEvents = useMemo(() => {
    let filtered = getUpcomingEvents();
    if (selectedCategory !== 'All') {
      filtered = filtered.filter(event => event.category === selectedCategory);
    }
    if (sortBy === 'name') {
      filtered = [...filtered].sort((a, b) => a.title.localeCompare(b.title));
    }
    return filtered;
  }, [selectedCategory, sortBy]);
  const pastEvents = useMemo(() => {
    let filtered = getPastEvents();
    if (selectedCategory !== 'All') {
      filtered = filtered.filter(event => event.category === selectedCategory);
    }
    if (sortBy === 'name') {
      filtered = [...filtered].sort((a, b) => a.title.localeCompare(b.title));
    }
    return filtered;
  }, [selectedCategory, sortBy]);
  const featuredEvent = upcomingEvents.find(e => e.featured);
  const getCategoryColor = (category: string) => {
    const colors: Record<string, string> = {
      Music: 'from-purple-500 to-pink-500',
      'Arts & Culture': 'from-amber-500 to-orange-500',
      Sports: 'from-emerald-500 to-teal-500',
      'Food & Drink': 'from-rose-500 to-red-500',
      Business: 'from-blue-500 to-indigo-500',
      Family: 'from-green-500 to-lime-500'
    };
    return colors[category] || 'from-gray-500 to-gray-600';
  };
  const renderEventCard = (event: Event, index: number, isPast: boolean = false) => <motion.div key={event.id} initial={{
    y: 20,
    opacity: 0
  }} animate={{
    y: 0,
    opacity: 1
  }} transition={{
    delay: index * 0.05
  }} data-id="element-814">
      <Link to={`/events/${event.slug}`} className="group block" data-id="element-815">
        <div className={`border-2 border-gray-100 hover:border-teal-400 transition-smooth overflow-hidden h-full flex flex-col bg-white shadow-elegant hover:shadow-elegant-hover ${isPast ? 'opacity-75' : ''}`} data-id="element-816">
          <div className="relative aspect-[4/3] overflow-hidden" data-id="element-817">
            <img src={event.image} alt={event.title} className="w-full h-full object-cover group-hover:scale-105 transition-smooth" data-id="element-818" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" data-id="element-819" />

            {/* Category Badge */}
            <div className="absolute top-4 left-4" data-id="element-820">
              <span className={`px-4 py-2 bg-gradient-to-r ${getCategoryColor(event.category)} text-white text-xs font-bold rounded-full shadow-lg`} data-id="element-821">
                {event.category.toUpperCase()}
              </span>
            </div>

            {/* Status Badge */}
            <div className="absolute top-4 right-4" data-id="element-822">
              {isPast ? <span className="px-4 py-2 bg-gray-800/90 backdrop-blur-sm text-white text-xs font-bold rounded-full" data-id="element-823">
                  COMPLETED
                </span> : event.featured ? <span className="px-4 py-2 bg-gradient-to-r from-amber-500 to-orange-500 text-white text-xs font-bold rounded-full shadow-lg" data-id="element-824">
                  FEATURED
                </span> : null}
            </div>

            {/* Ticket Price */}
            <div className="absolute bottom-4 left-4 right-4" data-id="element-825">
              <div className="flex items-center gap-2 bg-white/95 backdrop-blur-sm px-4 py-2 rounded-full shadow-lg" data-id="element-826">
                <Ticket className="w-4 h-4 text-teal-600" strokeWidth={2.5} data-id="element-827" />
                <span className="font-bold text-charcoal text-sm" data-id="element-828">
                  {isPast ? 'Event Completed' : `From KES ${Math.min(...event.tickets.map(t => t.price)).toLocaleString()}`}
                </span>
              </div>
            </div>
          </div>

          <div className="p-6 flex-1 flex flex-col" data-id="element-829">
            <div className="flex items-center gap-3 text-xs text-gray-600 mb-3" data-id="element-830">
              <div className="flex items-center gap-1" data-id="element-831">
                <Calendar className="w-3 h-3" strokeWidth={2} data-id="element-832" />
                <span className="font-medium" data-id="element-833">
                  {new Date(event.date).toLocaleDateString('en-US', {
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric'
                })}
                </span>
              </div>
              <div className="flex items-center gap-1" data-id="element-834">
                <Clock className="w-3 h-3" strokeWidth={2} data-id="element-835" />
                <span data-id="element-836">{event.time}</span>
              </div>
            </div>

            <h3 className="font-display text-2xl font-bold text-charcoal mb-3 group-hover:text-teal-600 transition-colors line-clamp-2" data-id="element-837">
              {event.title}
            </h3>

            <p className="text-gray-600 text-sm line-clamp-2 mb-4 flex-1" data-id="element-838">
              {event.description}
            </p>

            <div className="flex items-center gap-2 text-sm text-gray-500 mb-4" data-id="element-839">
              <MapPin className="w-4 h-4" strokeWidth={2} data-id="element-840" />
              <span className="line-clamp-1" data-id="element-841">{event.venue.name}</span>
            </div>

            <div className="pt-4 border-t border-gray-100" data-id="element-842">
              <span className="text-sm font-semibold text-teal-600 group-hover:text-teal-700" data-id="element-843">
                {isPast ? 'View recap →' : 'View details →'}
              </span>
            </div>
          </div>
        </div>
      </Link>
    </motion.div>;
  return <div className="min-h-screen bg-gradient-to-b from-white to-gray-50" data-id="element-844">
      {/* Hero */}
      <div className="border-b border-gray-200 bg-white" data-id="element-845">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-20 md:py-32" data-id="element-846">
          <motion.div initial={{
          y: 20,
          opacity: 0
        }} animate={{
          y: 0,
          opacity: 1
        }} transition={{
          duration: 0.6
        }} className="max-w-3xl" data-id="element-847">
            <div className="inline-block mb-6" data-id="element-848">
              <span className="px-4 py-2 bg-gradient-to-r from-teal-500 to-emerald-500 text-white text-sm font-bold rounded-full" data-id="element-849">
                WHAT'S HAPPENING
              </span>
            </div>
            <h1 className="font-display text-6xl md:text-8xl font-bold mb-6 leading-tight" data-id="element-850">
              <span className="gradient-text-ocean" data-id="element-851">Events</span> in Nairobi
            </h1>
            <p className="text-xl md:text-2xl text-gray-600 leading-relaxed" data-id="element-852">
              Discover concerts, festivals, conferences, and cultural events
              happening in Africa's most vibrant city.
            </p>
          </motion.div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-16" data-id="element-853">
        {/* Featured Event */}
        {featuredEvent && <section className="mb-16" data-id="element-854">
            <div className="flex items-center gap-3 mb-6" data-id="element-855">
              <div className="h-1 w-12 bg-gradient-to-r from-amber-500 to-orange-500 rounded-full" data-id="element-856" />
              <span className="text-sm font-bold text-gray-600 uppercase tracking-wider" data-id="element-857">
                Featured Event
              </span>
            </div>

            <Link to={`/events/${featuredEvent.slug}`} className="group" data-id="element-858">
              <div className="grid md:grid-cols-2 gap-8 border-2 border-gray-100 hover:border-teal-400 transition-smooth overflow-hidden bg-white shadow-elegant hover:shadow-elegant-hover" data-id="element-859">
                <div className="relative aspect-[4/3] md:aspect-auto overflow-hidden" data-id="element-860">
                  <img src={featuredEvent.image} alt={featuredEvent.title} className="w-full h-full object-cover group-hover:scale-105 transition-smooth" data-id="element-861" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" data-id="element-862" />
                  <div className="absolute top-6 left-6" data-id="element-863">
                    <span className={`px-5 py-2.5 bg-gradient-to-r ${getCategoryColor(featuredEvent.category)} text-white text-sm font-bold rounded-full shadow-lg`} data-id="element-864">
                      {featuredEvent.category.toUpperCase()}
                    </span>
                  </div>
                </div>

                <div className="p-8 flex flex-col justify-center" data-id="element-865">
                  <div className="flex items-center gap-4 text-sm text-gray-600 mb-4" data-id="element-866">
                    <div className="flex items-center gap-2" data-id="element-867">
                      <Calendar className="w-4 h-4 text-teal-600" strokeWidth={2} data-id="element-868" />
                      <span className="font-medium" data-id="element-869">
                        {new Date(featuredEvent.date).toLocaleDateString('en-US', {
                      month: 'long',
                      day: 'numeric',
                      year: 'numeric'
                    })}
                      </span>
                    </div>
                    <div className="flex items-center gap-2" data-id="element-870">
                      <MapPin className="w-4 h-4 text-teal-600" strokeWidth={2} data-id="element-871" />
                      <span className="font-medium" data-id="element-872">
                        {featuredEvent.venue.name}
                      </span>
                    </div>
                  </div>

                  <h2 className="font-display text-4xl font-bold text-charcoal mb-4 group-hover:text-teal-600 transition-colors" data-id="element-873">
                    {featuredEvent.title}
                  </h2>

                  <p className="text-gray-700 leading-relaxed mb-6 text-lg" data-id="element-874">
                    {featuredEvent.description}
                  </p>

                  <div className="flex items-center gap-2 text-sm mb-6" data-id="element-875">
                    <Ticket className="w-5 h-5 text-teal-600" strokeWidth={2} data-id="element-876" />
                    <span className="font-bold text-charcoal" data-id="element-877">
                      From KES{' '}
                      {Math.min(...featuredEvent.tickets.map(t => t.price)).toLocaleString()}
                    </span>
                  </div>

                  <span className="text-teal-600 font-semibold group-hover:text-teal-700" data-id="element-878">
                    View event details →
                  </span>
                </div>
              </div>
            </Link>
          </section>}

        {/* Filters */}
        <section className="mb-12" data-id="element-879">
          <div className="flex flex-col md:flex-row gap-4 items-start md:items-center justify-between" data-id="element-880">
            <div className="flex flex-wrap gap-2" data-id="element-881">
              {eventCategories.map(category => <button key={category} onClick={() => setSelectedCategory(category)} className={`px-5 py-2.5 text-sm font-bold rounded-full transition-smooth ${selectedCategory === category ? 'bg-gradient-to-r from-teal-500 to-emerald-500 text-white shadow-lg' : 'bg-white text-gray-700 border-2 border-gray-200 hover:border-teal-400'}`} data-id="element-882">
                  {category}
                </button>)}
            </div>
            <select value={sortBy} onChange={e => setSortBy(e.target.value as 'date' | 'name')} className="px-4 py-2.5 bg-white border-2 border-gray-200 rounded-lg focus:border-teal-500 focus:outline-none transition-colors text-sm font-medium" data-id="element-883">
              <option value="date" data-id="element-884">Sort by Date</option>
              <option value="name" data-id="element-885">Sort by Name</option>
            </select>
          </div>
        </section>

        {/* Upcoming Events */}
        <section className="mb-16" data-id="element-886">
          <div className="flex items-center gap-3 mb-8" data-id="element-887">
            <div className="h-1 w-12 bg-gradient-to-r from-teal-500 to-emerald-500 rounded-full" data-id="element-888" />
            <h2 className="font-display text-3xl font-bold text-charcoal" data-id="element-889">
              Upcoming Events
            </h2>
          </div>

          {upcomingEvents.length > 0 ? <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-890">
              {upcomingEvents.map((event, index) => renderEventCard(event, index, false))}
            </div> : <div className="text-center py-16 bg-white rounded-2xl border-2 border-gray-100" data-id="element-891">
              <p className="font-display text-3xl font-bold text-gray-400 mb-4" data-id="element-892">
                No upcoming events
              </p>
              <p className="text-gray-600 mb-6" data-id="element-893">
                Try selecting a different category
              </p>
              <button onClick={() => setSelectedCategory('All')} className="px-6 py-3 bg-gradient-to-r from-teal-500 to-emerald-500 text-white font-semibold rounded-full hover:shadow-lg transition-smooth" data-id="element-894">
                View All Events
              </button>
            </div>}
        </section>

        {/* Past Events */}
        {pastEvents.length > 0 && <section data-id="element-895">
            <div className="flex items-center justify-between mb-8" data-id="element-896">
              <div className="flex items-center gap-3" data-id="element-897">
                <div className="h-1 w-12 bg-gradient-to-r from-gray-400 to-gray-500 rounded-full" data-id="element-898" />
                <h2 className="font-display text-3xl font-bold text-charcoal" data-id="element-899">
                  Past Events
                </h2>
              </div>
              <button onClick={() => setShowPastEvents(!showPastEvents)} className="px-4 py-2 text-sm font-semibold text-gray-600 hover:text-charcoal transition-colors" data-id="element-900">
                {showPastEvents ? 'Hide' : 'Show'} ({pastEvents.length})
              </button>
            </div>

            {showPastEvents && <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-901">
                {pastEvents.map((event, index) => renderEventCard(event, index, true))}
              </div>}
          </section>}
      </div>

      <ScrollToTop data-id="element-902" />
      <Footer data-id="element-903" />
    </div>;
}